// src/components/FileCategoriesTab.jsx
// Admin tab for managing file category folder structure (with nesting)

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, X, Loader2, Folder, FolderOpen, Edit2, Trash2, Check, 
  ChevronDown, ChevronRight, Eye, EyeOff, Lock, GripVertical,
  FolderPlus, ArrowUp, ArrowDown
} from 'lucide-react';
import { supabase } from '../lib/supabase';

// ============================================================================
// HOOK: useFileCategories
// ============================================================================
export function useFileCategories(organizationId) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('file_categories')
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching file categories:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const addCategory = async (categoryData) => {
    try {
      const { data, error } = await supabase
        .from('file_categories')
        .insert({
          organization_id: organizationId,
          ...categoryData,
        })
        .select()
        .single();

      if (error) throw error;
      setCategories(prev => [...prev, data].sort((a, b) => a.sort_order - b.sort_order));
      return data;
    } catch (err) {
      console.error('Error adding file category:', err);
      throw err;
    }
  };

  const updateCategory = async (categoryId, updates) => {
    try {
      const { data, error } = await supabase
        .from('file_categories')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', categoryId)
        .select()
        .single();

      if (error) throw error;
      setCategories(prev => prev.map(c => c.id === categoryId ? data : c).sort((a, b) => a.sort_order - b.sort_order));
      return data;
    } catch (err) {
      console.error('Error updating file category:', err);
      throw err;
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      const { error } = await supabase
        .from('file_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
      // Also remove any children (cascade should handle this, but update local state)
      setCategories(prev => prev.filter(c => c.id !== categoryId && c.parent_id !== categoryId));
    } catch (err) {
      console.error('Error deleting file category:', err);
      throw err;
    }
  };

  // Build tree structure from flat list
  const categoryTree = buildTree(categories);

  return { 
    categories,       // flat list
    categoryTree,     // nested tree
    loading, 
    addCategory, 
    updateCategory, 
    deleteCategory,
    refresh: fetchCategories
  };
}

// Build nested tree from flat category list
function buildTree(categories) {
  const map = {};
  const roots = [];

  // First pass: index by id
  categories.forEach(cat => {
    map[cat.id] = { ...cat, children: [] };
  });

  // Second pass: build parent-child relationships
  categories.forEach(cat => {
    if (cat.parent_id && map[cat.parent_id]) {
      map[cat.parent_id].children.push(map[cat.id]);
    } else if (!cat.parent_id) {
      roots.push(map[cat.id]);
    }
  });

  // Sort children by sort_order at each level
  const sortChildren = (nodes) => {
    nodes.sort((a, b) => a.sort_order - b.sort_order);
    nodes.forEach(n => sortChildren(n.children));
  };
  sortChildren(roots);

  return roots;
}

// Get full display path for a category (e.g., "01 Legal / Corporate")
export function getCategoryPath(categoryId, categories) {
  const parts = [];
  let current = categories.find(c => c.id === categoryId);
  while (current) {
    parts.unshift(current.display_name);
    current = current.parent_id ? categories.find(c => c.id === current.parent_id) : null;
  }
  return parts.join(' / ');
}

// Get root prefix for file naming (e.g., "01" for anything under Legal)
export function getCategoryPrefix(categoryId, categories) {
  let current = categories.find(c => c.id === categoryId);
  while (current?.parent_id) {
    current = categories.find(c => c.id === current.parent_id);
  }
  return current ? String(current.sort_order).padStart(2, '0') : '08';
}


// ============================================================================
// COMPONENT: FileCategoriesTab
// ============================================================================
export default function FileCategoriesTab({ organizationId, primaryColor, secondaryColor, showToast }) {
  const { 
    categories, categoryTree, loading, 
    addCategory, updateCategory, deleteCategory 
  } = useFileCategories(organizationId);

  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: '', slug: '', description: '' });
  const [addingTo, setAddingTo] = useState(null); // null = adding top-level, or parent_id
  const [addForm, setAddForm] = useState({ display_name: '', slug: '', description: '', is_internal: false });
  const [saving, setSaving] = useState(false);

  // Auto-expand all top-level on load
  useEffect(() => {
    if (categoryTree.length > 0 && Object.keys(expanded).length === 0) {
      const exp = {};
      categoryTree.forEach(c => { exp[c.id] = true; });
      setExpanded(exp);
    }
  }, [categoryTree]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Auto-generate slug from display name
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Get next sort order for a given parent
  const getNextSortOrder = (parentId) => {
    const siblings = categories.filter(c => 
      parentId ? c.parent_id === parentId : !c.parent_id
    );
    if (siblings.length === 0) return parentId ? 1 : 10;
    return Math.max(...siblings.map(s => s.sort_order)) + 1;
  };

  // Add category
  const handleAdd = async () => {
    if (!addForm.display_name.trim()) return;
    setSaving(true);
    try {
      const slug = addForm.slug.trim() || generateSlug(addForm.display_name);
      await addCategory({
        parent_id: addingTo || null,
        display_name: addForm.display_name.trim(),
        slug,
        description: addForm.description.trim() || null,
        is_internal: addForm.is_internal,
        sort_order: getNextSortOrder(addingTo),
      });
      setAddForm({ display_name: '', slug: '', description: '', is_internal: false });
      setAddingTo(null);
      showToast(addingTo ? 'Subfolder added' : 'Category added');
    } catch (err) {
      if (err.message?.includes('duplicate key') || err.message?.includes('unique')) {
        showToast('A category with that name already exists at this level', 'error');
      } else {
        showToast(err.message, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const handleStartEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({
      display_name: cat.display_name,
      slug: cat.slug,
      description: cat.description || '',
    });
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editForm.display_name.trim()) return;
    setSaving(true);
    try {
      await updateCategory(editingId, {
        display_name: editForm.display_name.trim(),
        slug: editForm.slug.trim() || generateSlug(editForm.display_name),
        description: editForm.description.trim() || null,
      });
      setEditingId(null);
      showToast('Category updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async (cat) => {
    const childCount = categories.filter(c => c.parent_id === cat.id).length;
    const msg = childCount > 0
      ? `Delete "${cat.display_name}" and its ${childCount} subfolder(s)? Files tagged with these categories will become uncategorized.`
      : `Delete "${cat.display_name}"? Files tagged with this category will become uncategorized.`;
    
    if (!confirm(msg)) return;

    try {
      await deleteCategory(cat.id);
      showToast('Category deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Toggle active/inactive
  const handleToggleActive = async (cat) => {
    try {
      await updateCategory(cat.id, { is_active: !cat.is_active });
      showToast(cat.is_active ? 'Category hidden' : 'Category restored');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Move sort order up/down among siblings
  const handleMove = async (cat, direction) => {
    const siblings = categories
      .filter(c => cat.parent_id ? c.parent_id === cat.parent_id : !c.parent_id)
      .sort((a, b) => a.sort_order - b.sort_order);

    const idx = siblings.findIndex(s => s.id === cat.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;

    if (swapIdx < 0 || swapIdx >= siblings.length) return;

    const other = siblings[swapIdx];

    try {
      // Swap sort_order values
      await updateCategory(cat.id, { sort_order: other.sort_order });
      await updateCategory(other.id, { sort_order: cat.sort_order });
      showToast('Order updated');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Render a single category row
  const renderCategory = (cat, depth = 0) => {
    const hasChildren = cat.children && cat.children.length > 0;
    const isExpanded = expanded[cat.id];
    const isEditing = editingId === cat.id;
    const isAddingChild = addingTo === cat.id;

    return (
      <div key={cat.id}>
        <div 
          className={`flex items-center gap-2 py-2 px-3 border-b hover:bg-gray-50 transition-colors group ${!cat.is_active ? 'opacity-50' : ''}`}
          style={{ paddingLeft: `${12 + depth * 24}px` }}
        >
          {/* Expand/collapse toggle */}
          <button 
            onClick={() => toggleExpand(cat.id)}
            className="w-5 h-5 flex items-center justify-center flex-shrink-0"
            style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          >
            {isExpanded 
              ? <ChevronDown className="w-4 h-4" style={{ color: secondaryColor }} />
              : <ChevronRight className="w-4 h-4" style={{ color: secondaryColor }} />
            }
          </button>

          {/* Folder icon */}
          {isExpanded && hasChildren
            ? <FolderOpen className="w-4 h-4 flex-shrink-0" style={{ color: cat.is_internal ? '#ef4444' : primaryColor }} />
            : <Folder className="w-4 h-4 flex-shrink-0" style={{ color: cat.is_internal ? '#ef4444' : primaryColor }} />
          }

          {isEditing ? (
            /* Edit mode */
            <div className="flex-1 flex items-center gap-2">
              <input
                value={editForm.display_name}
                onChange={e => setEditForm(p => ({ ...p, display_name: e.target.value }))}
                className="border rounded px-2 py-1 text-sm flex-1"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') setEditingId(null);
                }}
              />
              <button 
                onClick={handleSaveEdit} 
                disabled={saving}
                className="p-1 hover:bg-green-50 rounded text-green-600"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button onClick={() => setEditingId(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            /* Display mode */
            <>
              <span className="flex-1 text-sm font-medium">
                {cat.display_name}
              </span>

              {cat.is_internal && (
                <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-600 rounded flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Internal
                </span>
              )}

              {!cat.is_active && (
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                  Hidden
                </span>
              )}

              {cat.description && (
                <span className="text-xs hidden lg:inline" style={{ color: secondaryColor }}>
                  {cat.description}
                </span>
              )}

              {/* Actions (visible on hover) */}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleMove(cat, 'up')}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Move up"
                >
                  <ArrowUp className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                </button>
                <button
                  onClick={() => handleMove(cat, 'down')}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Move down"
                >
                  <ArrowDown className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                </button>
                <button
                  onClick={() => {
                    setAddingTo(cat.id);
                    setAddForm({ display_name: '', slug: '', description: '', is_internal: cat.is_internal });
                    setExpanded(prev => ({ ...prev, [cat.id]: true }));
                  }}
                  className="p-1 hover:bg-blue-50 rounded"
                  title="Add subfolder"
                  style={{ color: primaryColor }}
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleStartEdit(cat)}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Edit"
                >
                  <Edit2 className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                </button>
                <button
                  onClick={() => handleToggleActive(cat)}
                  className="p-1 hover:bg-gray-100 rounded"
                  title={cat.is_active ? 'Hide category' : 'Show category'}
                >
                  {cat.is_active 
                    ? <EyeOff className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                    : <Eye className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                  }
                </button>
                <button
                  onClick={() => handleDelete(cat)}
                  className="p-1 hover:bg-red-50 rounded text-red-500"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Inline add subfolder form */}
        {isAddingChild && (
          <div 
            className="flex items-center gap-2 py-2 px-3 bg-blue-50 border-b"
            style={{ paddingLeft: `${12 + (depth + 1) * 24}px` }}
          >
            <FolderPlus className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
            <input
              value={addForm.display_name}
              onChange={e => setAddForm(p => ({ ...p, display_name: e.target.value }))}
              placeholder="Subfolder name..."
              className="border rounded px-2 py-1 text-sm flex-1"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') setAddingTo(null);
              }}
            />
            <input
              value={addForm.description}
              onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Description (optional)"
              className="border rounded px-2 py-1 text-sm flex-1 hidden md:block"
            />
            <button 
              onClick={handleAdd}
              disabled={saving || !addForm.display_name.trim()}
              className="p-1 hover:bg-green-50 rounded text-green-600 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button onClick={() => setAddingTo(null)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Children */}
        {isExpanded && hasChildren && cat.children.map(child => renderCategory(child, depth + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: primaryColor }} />
        <span style={{ color: secondaryColor }}>Loading categories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold text-lg" style={{ color: primaryColor }}>File Categories</h2>
        <p className="text-sm mt-1" style={{ color: secondaryColor }}>
          Define the folder structure for organizing deal files. This structure will be used in the files view and when syncing to SharePoint. 
          Hover over a category to see actions â€” you can add subfolders, rename, reorder, hide, or delete.
        </p>
      </div>

      {/* Category Tree */}
      <div className="border rounded-lg overflow-hidden">
        {categoryTree.length === 0 ? (
          <div className="text-center py-8" style={{ color: secondaryColor }}>
            <Folder className="w-10 h-10 mx-auto mb-2" />
            <p>No categories defined yet.</p>
          </div>
        ) : (
          categoryTree.map(cat => renderCategory(cat, 0))
        )}
      </div>

      {/* Add top-level category */}
      {addingTo === '__root__' ? (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <h3 className="font-medium text-sm mb-3" style={{ color: primaryColor }}>Add Top-Level Category</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1 font-medium">Display Name *</label>
                <input
                  value={addForm.display_name}
                  onChange={e => setAddForm(p => ({ ...p, display_name: e.target.value }))}
                  placeholder="e.g., 10 Operations"
                  className="w-full border rounded px-3 py-2 text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs mb-1 font-medium">Slug (auto-generated)</label>
                <input
                  value={addForm.slug || generateSlug(addForm.display_name)}
                  onChange={e => setAddForm(p => ({ ...p, slug: e.target.value }))}
                  placeholder="Auto-generated from name"
                  className="w-full border rounded px-3 py-2 text-sm font-mono text-gray-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs mb-1 font-medium">Description</label>
              <input
                value={addForm.description}
                onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                placeholder="What goes in this category?"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox"
                checked={addForm.is_internal}
                onChange={e => setAddForm(p => ({ ...p, is_internal: e.target.checked }))}
                className="rounded"
              />
              <Lock className="w-3.5 h-3.5 text-red-500" />
              <span className="text-sm">Internal only (hidden from sellers and buyer data rooms)</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button 
              onClick={() => {
                setAddingTo(null);
                setAddForm({ display_name: '', slug: '', description: '', is_internal: false });
              }}
              className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={saving || !addForm.display_name.trim()}
              className="px-3 py-1.5 text-white rounded text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
              style={{ backgroundColor: primaryColor }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Category
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => {
            setAddingTo('__root__');
            setAddForm({ display_name: '', slug: '', description: '', is_internal: false });
          }}
          className="flex items-center gap-2 text-sm px-3 py-2 border border-dashed rounded-lg hover:bg-gray-50 transition-colors w-full justify-center"
          style={{ color: primaryColor, borderColor: primaryColor }}
        >
          <Plus className="w-4 h-4" /> Add Top-Level Category
        </button>
      )}

      {/* Legend */}
      <div className="text-xs space-y-1 pt-2 border-t" style={{ color: secondaryColor }}>
        <p>
          <strong>Tip:</strong> Hover over any category to see actions. 
          Use <FolderPlus className="w-3 h-3 inline" /> to add subfolders, 
          <ArrowUp className="w-3 h-3 inline" /><ArrowDown className="w-3 h-3 inline" /> to reorder, 
          <EyeOff className="w-3 h-3 inline" /> to hide without deleting.
        </p>
        <p>
          Categories marked <Lock className="w-3 h-3 inline text-red-500" /> <strong>Internal</strong> will never appear in seller portals or buyer data rooms.
        </p>
      </div>
    </div>
  );
}
